// recent-team.js - which team you are working on, across screens.
//
// THE PROBLEM. The club has 67 teams. A director doing a round of work on one
// squad opens Fast grading, then Team files, then Match replies, then Player
// access, and picks that same team out of 67 on every single screen, because
// each one keeps its own idea of what is selected and starts with nothing.
//
// Two of them were worse than nothing: team-files.js and squad-replies.js
// defaulted to `_teams[0]`, the first team in id order, which for Dragons is
// some U10 side the director almost never wants. An arbitrary default is not a
// convenience, it is a wrong answer that looks like an answer.
//
// WHAT THIS IS NOT. It is not a global "current team" that screens silently
// enter. A screen that puts you inside a team you did not choose is worse than
// one that asks, because you can act on the wrong squad without noticing. This
// is a MEMORY: screens offer it, default to it where they already defaulted to
// something arbitrary, and always let you pick another.
//
// LIFETIME. Module state, so it lives as long as the page. That is deliberate:
// signing out reloads the page (see signOut in app.js), which clears it, so
// one person's last team can never greet the next person on a shared tablet.
// It also means it never goes stale across days.
//
// No imports, so nothing can get a circular dependency out of it.

let _teamId = null;

/** Remember the team being worked on. Call it wherever a team is chosen. */
export function rememberTeam(teamId) {
  const id = (teamId == null ? '' : String(teamId)).trim();
  _teamId = id || null;
  return _teamId;
}

/**
 * The team last worked on, or null.
 *
 * Pass the teams currently available and it will only return one that is
 * actually in that list: a remembered team can have been deleted, renamed or
 * belong to a club no longer being worn, and handing a screen an id it cannot
 * show is how you get an empty page with no explanation.
 */
export function recentTeamId(available) {
  if (!_teamId) return null;
  if (!available) return _teamId;
  const ids = Array.isArray(available)
    ? available.map(t => (typeof t === 'string' ? t : t?.id))
    : [];
  return ids.includes(_teamId) ? _teamId : null;
}

/** Forget it. For sign-out paths that do not reload, and for tests. */
export function clearRecentTeam() { _teamId = null; }
